'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { useEffect, useRef, useState } from 'react';

import { useLogger, useTracking } from '@/analytics';
import { ButtonLink } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';
import { AffiliateId } from '@/models';

import { headlineFor, IMPACT_FOOTNOTE, IMPACT_LABELS, IMPACT_SOURCES, milestoneTier, titleCase } from '../data';
import { LEVELS, metersToFeet, RunSummary } from '../engine';
import styles from '../SolarSurge.module.css';

import { deliverScoreCardFile, renderScoreCardFile, shareScoreCard } from './shareScoreCard';

type Props = {
  onPlayAgain: () => void;
  summary: RunSummary | null;
};

const fmt = (value: number, digits = 0): string =>
  value.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits });

const Impact = ({ icon: Icon, label, value }: { icon: DS.IIconComponent; label: string; value: string }) => (
  <DS.Card variant="secondary">
    {/* Icon tile top-aligns with the visible cap/ascender of the value text. flex-start alone aligns to
        the text's line box (leaving the tile too high), so the tile is nudged down by the leading above
        the caps — larger on desktop where the value is bigger. */}
    <x.div alignItems="flex-start" display="flex" gap={3} p={3}>
      <x.div alignSelf="flex-start" flexShrink={0} mt={{ _: '10px', md: '8px' }}>
        <DS.Status bg="uiStatus.done" color="white" icon={Icon} size="sm" />
      </x.div>
      <x.div>
        {/* Mobile keeps the large value (d1); desktop is smaller (h2). */}
        <x.div lineHeight={1} text={{ _: 'd1', md: 'h2' }}>
          {value}
        </x.div>
        <x.div color="uiHelperCopy" mt={1} text="body12">
          {label}
        </x.div>
      </x.div>
    </x.div>
  </DS.Card>
);

export const GameOverModal = ({ onPlayAgain, summary }: Props) => {
  const { addError } = useLogger();
  const { trackEvent } = useTracking();
  const open = summary !== null;

  const logoRef = useRef<HTMLSpanElement>(null);
  const [shareFile, setShareFile] = useState<File | null>(null);

  // Pre-render this run's card so a share tap fires synchronously (iOS Safari requires
  // navigator.share inside the tap gesture, so the file must already exist at click time).
  useEffect(() => {
    let active = true;
    setShareFile(null);
    if (summary) {
      const logoSvg = logoRef.current?.querySelector('svg')?.outerHTML;
      // Guard against a superseded run or an unmount resolving late and setting a stale card.
      renderScoreCardFile({ logoSvg, summary })
        .then((file) => (active ? setShareFile(file) : undefined))
        .catch((err) => addError(err));
    }
    return () => {
      active = false;
    };
  }, [addError, summary]);

  if (!summary) return null;

  const tier = milestoneTier(summary.energyKwh);
  const level = LEVELS[Math.min(LEVELS.length, Math.max(1, summary.topLevel)) - 1];
  const headline = headlineFor(summary.isHighScore);

  const handleShare = () => {
    trackEvent(TRACKING.SOLAR_SURGE_CLICKED_SHARE, {
      energyKwh: Math.round(summary.energyKwh),
      isHighScore: summary.isHighScore,
    });
    const logoSvg = logoRef.current?.querySelector('svg')?.outerHTML;
    const delivery = shareFile
      ? deliverScoreCardFile(shareFile, summary.energyKwh)
      : shareScoreCard({ logoSvg, summary });
    delivery.catch((err) => {
      // user dismissed the native share sheet — not an error
      if (err instanceof DOMException && err.name === 'AbortError') return;
      addError(err);
    });
  };

  return (
    <DS.Modal open={open} size="md">
      <DS.ModalBody className={styles.summaryBody}>
        <x.div display="flex" flexDirection="column" gap={5}>
          {/* Headline lives inside the scrolling body (not a sticky ModalHeader) so it scrolls away on mobile.
              d1 renders 32px on mobile and 44px on desktop. */}
          <DS.Text as="h2" text="d1">
            {headline}
          </DS.Text>
          {summary.isHighScore && (
            <x.div alignItems="center" color="uiSuccess" display="flex" gap={2}>
              <DS.IconStarFill />
              <DS.Text color="uiSuccess" text={{ _: 'body14', md: 'body16' }}>
                New personal best: {fmt(summary.energyKwh, 1)} kWh
              </DS.Text>
            </x.div>
          )}

          <x.div>
            <DS.StatisticGroup aria-label="Run results">
              <x.div display="grid" gap={4} gridTemplateColumns="1fr 1fr">
                <DS.Statistic label="Energy banked" size="xl" unit="kWh" value={fmt(summary.energyKwh, 1)} />
                <DS.Statistic label="Distance flown" size="xl" unit="ft" value={fmt(metersToFeet(summary.distanceM))} />
              </x.div>
            </DS.StatisticGroup>
            <DS.Text as="p" color="uiHeadingCopy" mt={2} text="body16">
              <x.b>{tier.label}</x.b>
              <x.span> — {tier.copy}</x.span>
            </DS.Text>
          </x.div>

          <DS.Divider />

          <x.div>
            <DS.Text as="p" color="uiHeadingCopy" text="body16">
              Made it to Level {summary.topLevel} — {titleCase(summary.topLevelTitle)}
            </DS.Text>
            <DS.Text as="p" color="uiBodyCopy" mt={1} text="body14">
              <x.b>{level.concept}:</x.b>
              <x.span> {level.blurb}</x.span>
            </DS.Text>
          </x.div>

          <DS.Divider />

          <x.div>
            <DS.Text as="p" color="uiBodyCopy" mb={3} text="body14">
              <x.span>In the real world, generating </x.span>
              <x.b>{fmt(summary.energyKwh, 1)} kWh</x.b>
              <x.span> of solar means roughly:</x.span>
            </DS.Text>
            <x.div display="grid" gap={4} gridTemplateColumns={{ _: '1fr', md: 'repeat(2, 1fr)' }}>
              <Impact icon={DS.IconLeafFill} label={IMPACT_LABELS.co2} value={`${fmt(summary.co2Kg, 1)} kg`} />
              <Impact icon={DS.IconMoneyFill} label={IMPACT_LABELS.money} value={`$${fmt(summary.dollars, 2)}`} />
              <Impact icon={DS.IconLightningFill} label={IMPACT_LABELS.phone} value={fmt(summary.phoneCharges)} />
              <Impact icon={DS.IconHouseFill} label={IMPACT_LABELS.home} value={`${fmt(summary.homeHours, 1)} hrs`} />
            </x.div>
            <DS.Text as="p" color="uiHelperCopy" mt={3} text="body9">
              <x.span>{IMPACT_FOOTNOTE} </x.span>
              {IMPACT_SOURCES.map((source, index) => (
                <x.span key={source.label}>
                  {index > 0 && <x.span>; </x.span>}
                  <x.a href={source.href} rel="noopener noreferrer" target="_blank" textDecoration="underline">
                    {source.label}
                  </x.a>
                  <x.span> ({source.note})</x.span>
                </x.span>
              ))}
              <x.span>.</x.span>
            </DS.Text>
          </x.div>
        </x.div>
      </DS.ModalBody>
      <DS.ModalFooter>
        <x.div display="flex" flexDirection="column" gap={4} w="100%">
          {/* Desktop: three buttons on one row. Mobile: Play again + Share on top, estimates full-width below.
              alignItems="start" keeps the buttons top-aligned so the estimates column can carry the
              "No contact info needed" caption directly beneath it. */}
          <x.div alignItems="start" display="grid" gap={4} gridTemplateColumns={{ _: '1fr 1fr', md: '1fr 1fr 1fr' }}>
            <DS.Button
              className={styles.playAgain}
              fullWidth
              icon={DS.IconRefresh}
              onClick={onPlayAgain}
              size="md"
              variant="cta"
            >
              Play again
            </DS.Button>
            <DS.Button fullWidth icon={DS.IconExport} onClick={handleShare} size="md" variant="secondary">
              Share score card
            </DS.Button>
            <x.div
              alignItems="center"
              display="flex"
              flexDirection="column"
              gap={1}
              gridColumn={{ _: '1 / -1', md: 'auto' }}
            >
              <ButtonLink
                affiliateId={AffiliateId.MINITOOLS}
                className={styles.estimateCta}
                event={{ name: TRACKING.SOLAR_SURGE_CLICKED_CTA }}
                fullWidth
                path="/quote-builder"
                size="md"
                variant="cta"
              >
                See solar estimates for your home
              </ButtonLink>
              <DS.Text color="uiHelperCopy" text="body12" textAlign="center">
                No contact info needed
              </DS.Text>
            </x.div>
          </x.div>
        </x.div>
      </DS.ModalFooter>
      {/* hidden render of the current wordmark — the share card rasterizes this exact SVG */}
      <x.span display="none" ref={logoRef}>
        <DS.IconLogo />
      </x.span>
    </DS.Modal>
  );
};
